import React, {useCallback, useState} from 'react';
import {View, StyleSheet, ScrollView} from 'react-native';
import {
  Text,
  Button,
  Divider,
  Snackbar,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

const SubscriptionInvoiceScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {orgUnitId, orgUnitName, subscriptionId, planFamily, planVariant} =
    route.params || {};

  const [snackbar, setSnackbar] = useState('');

  const listPrice = parseFloat(planVariant?.listPrice || 0);
  const netPrice = parseFloat(planVariant?.netPrice || 0);
  const discountPct = parseFloat(planVariant?.discountPct || 0);
  const discountAmount = listPrice - netPrice;
  const taxRate = 0.18; // 18% GST
  const taxAmount = parseFloat((netPrice * taxRate).toFixed(2));
  const grandTotal = parseFloat((netPrice + taxAmount).toFixed(2));

  const durationLabel =
    planVariant?.durationCount === 1
      ? `1 ${t('subscription.month')}`
      : `${planVariant?.durationCount} ${t('subscription.months')}`;

  const handlePay = useCallback(() => {
    navigation.navigate('PaymentGateway', {
      orgUnitId,
      orgUnitName,
      subscriptionId,
      planFamily,
      planVariant,
      invoiceAmount: grandTotal,
      netPrice,
      taxAmount,
      discountAmount,
    });
  }, [
    navigation,
    orgUnitId,
    orgUnitName,
    subscriptionId,
    planFamily,
    planVariant,
    grandTotal,
    netPrice,
    taxAmount,
    discountAmount,
  ]);

  return (
    <View style={styles.container}>
      <GradientHeader title={t('subscription.invoice')} onBack={() => navigation.goBack()} />

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Invoice header */}
        <View style={styles.invoiceHeader}>
          <MaterialCommunityIcons
            name="receipt"
            size={32}
            color={colors.primary}
          />
          <View style={styles.invoiceHeaderText}>
            <Text style={styles.invoiceTitle}>
              {t('subscription.proformaInvoice')}
            </Text>
            <Text style={styles.invoiceSubtitle}>{orgUnitName}</Text>
          </View>
        </View>

        <Divider style={styles.divider} />

        {/* Plan details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('subscription.planDetails')}</Text>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>{t('subscription.plan')}</Text>
            <Text style={styles.detailValue}>{planFamily?.name}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>{t('subscription.variant')}</Text>
            <Text style={styles.detailValue}>{planVariant?.name}</Text>
          </View>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>{t('subscription.duration')}</Text>
            <Text style={styles.detailValue}>{durationLabel}</Text>
          </View>
        </View>

        <Divider style={styles.divider} />

        {/* Line items table */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('subscription.lineItems')}</Text>

          {/* Header row */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, styles.itemCol]}>
              {t('subscription.item')}
            </Text>
            <Text style={[styles.tableHeaderText, styles.qtyCol]}>
              {t('subscription.qty')}
            </Text>
            <Text style={[styles.tableHeaderText, styles.amountCol]}>
              {t('subscription.amount')}
            </Text>
          </View>

          {/* Line item */}
          <View style={styles.tableRow}>
            <View style={styles.itemCol}>
              <Text style={styles.itemName}>{planFamily?.name} Plan</Text>
              <Text style={styles.itemDesc}>{planVariant?.name}</Text>
            </View>
            <Text style={styles.qtyCol}>1</Text>
            <Text style={styles.amountCol}>{'\u20B9'}{listPrice.toFixed(2)}</Text>
          </View>
        </View>

        <Divider style={styles.divider} />

        {/* Totals */}
        <View style={styles.totalsSection}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>{t('subscription.subtotal')}</Text>
            <Text style={styles.totalValue}>
              {'\u20B9'}{listPrice.toFixed(2)}
            </Text>
          </View>

          {discountAmount > 0 && (
            <View style={styles.totalRow}>
              <Text style={[styles.totalLabel, styles.discountLabel]}>
                {t('subscription.discount')} ({discountPct}%)
              </Text>
              <Text style={[styles.totalValue, styles.discountValue]}>
                -{'\u20B9'}{discountAmount.toFixed(2)}
              </Text>
            </View>
          )}

          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>
              {t('subscription.afterDiscount')}
            </Text>
            <Text style={styles.totalValue}>
              {'\u20B9'}{netPrice.toFixed(2)}
            </Text>
          </View>

          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>
              {t('subscription.gst')} (18%)
            </Text>
            <Text style={styles.totalValue}>
              {'\u20B9'}{taxAmount.toFixed(2)}
            </Text>
          </View>

          <Divider style={{marginVertical: spacing.sm}} />

          <View style={styles.totalRow}>
            <Text style={styles.grandTotalLabel}>
              {t('subscription.grandTotal')}
            </Text>
            <Text style={styles.grandTotalValue}>
              {'\u20B9'}{grandTotal.toFixed(2)}
            </Text>
          </View>
        </View>

        {/* Savings callout */}
        {discountAmount > 0 && (
          <View style={styles.savingsBox}>
            <MaterialCommunityIcons
              name="tag-check"
              size={20}
              color={colors.success}
            />
            <Text style={styles.savingsText}>
              {t('subscription.youSave')} {'\u20B9'}{discountAmount.toFixed(2)}{' '}
              ({discountPct}% {t('subscription.off')})
            </Text>
          </View>
        )}
      </ScrollView>

      {/* Pay button */}
      <View style={styles.bottomBar}>
        <View style={styles.bottomTotal}>
          <Text style={styles.bottomTotalLabel}>{t('subscription.total')}</Text>
          <Text style={styles.bottomTotalValue}>
            {'\u20B9'}{grandTotal.toFixed(2)}
          </Text>
        </View>
        <Button
          mode="contained"
          onPress={handlePay}
          buttonColor={colors.primary}
          contentStyle={styles.payButtonContent}
          style={styles.payButton}
          icon="credit-card-outline">
          {t('subscription.pay')}
        </Button>
      </View>

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  content: {flex: 1, padding: spacing.md},

  // Invoice header
  invoiceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    backgroundColor: colors.surface,
    borderRadius: 12,
  },
  invoiceHeaderText: {flex: 1},
  invoiceTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.text,
  },
  invoiceSubtitle: {
    fontSize: 14,
    color: colors.textSecondary,
    marginTop: 2,
  },

  divider: {marginVertical: spacing.md},

  // Sections
  section: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: spacing.md,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  detailLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
  },

  // Table
  tableHeader: {
    flexDirection: 'row',
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  tableHeaderText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
  },
  itemCol: {flex: 3},
  qtyCol: {flex: 1, textAlign: 'center', fontSize: 14, color: colors.text},
  amountCol: {flex: 2, textAlign: 'right', fontSize: 14, color: colors.text},
  itemName: {fontSize: 14, fontWeight: '600', color: colors.text},
  itemDesc: {fontSize: 12, color: colors.textSecondary, marginTop: 2},

  // Totals
  totalsSection: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: spacing.md,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  totalLabel: {fontSize: 14, color: colors.textSecondary},
  totalValue: {fontSize: 14, color: colors.text},
  discountLabel: {color: colors.success},
  discountValue: {color: colors.success, fontWeight: '600'},
  grandTotalLabel: {fontSize: 16, fontWeight: '700', color: colors.text},
  grandTotalValue: {fontSize: 18, fontWeight: '700', color: colors.primary},

  // Savings
  savingsBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.success + '12',
    borderRadius: 8,
    padding: spacing.sm + 4,
    marginTop: spacing.md,
    marginBottom: spacing.xxl,
  },
  savingsText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.success,
  },

  // Bottom bar
  bottomBar: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
    gap: spacing.md,
  },
  bottomTotal: {flex: 1},
  bottomTotalLabel: {fontSize: 12, color: colors.textSecondary},
  bottomTotalValue: {fontSize: 20, fontWeight: '700', color: colors.text},
  payButton: {borderRadius: 8, flex: 1},
  payButtonContent: {paddingVertical: spacing.xs},
});

export default SubscriptionInvoiceScreen;
